/**
 * TaskFlowTriageView.tsx
 *
 * Distraction-Free Single-Card Triage Review Interface.
 * 100% Theme-tokenized with fluid typography, keyboard accessibility,
 * Inbox Zero state, multi-locale translations, and rapid hotkeys (1-5, J/K, P, D, A).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { TaskFlowItem, TaskFlowPriority } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import { TaskFlowTriageQueue } from '../../../plugins/taskflow/engine/taskflow-triage-queue.js';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';
import { TaskFlowShortcutManager } from '../../../plugins/taskflow/engine/taskflow-shortcuts.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

export interface TaskFlowTriageViewProps {
  workspaceId: string;
  items: TaskFlowItem[];
  onSelectTask: (task: TaskFlowItem) => void;
}

export const TaskFlowTriageView: React.FC<TaskFlowTriageViewProps> = ({
  workspaceId,
  items,
  onSelectTask,
}) => {
  const { t, formatDate } = useTranslation();

  // Only review non-done, non-archived items in triage queue
  const pendingItems = useMemo(
    () => items.filter((i) => i.status !== 'done' && i.status !== 'archived'),
    [items]
  );

  const queue = useMemo(() => new TaskFlowTriageQueue(pendingItems), [pendingItems]);
  const [index, setIndex] = useState(0);

  const currentItem = queue.getCurrentItem();
  const progress = queue.getProgress();

  const handleApplyPatch = useCallback(
    (patch: Partial<TaskFlowItem>) => {
      if (!currentItem) return;
      globalTaskFlowStore.updateItem(currentItem.id, {
        patch,
        actorId: 'triage',
      });
      // Move to next item after triage action
      if (index < pendingItems.length - 1) {
        setIndex((prev) => prev + 1);
        queue.next();
      }
    },
    [currentItem, index, pendingItems.length, queue]
  );

  // Global key listener for triage view
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (TaskFlowShortcutManager.shouldIgnoreEvent(e.target as any)) {
        return;
      }

      const key = e.key.toUpperCase();

      if (key === 'J' || e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        queue.next();
        setIndex(queue.getCurrentIndex());
      } else if (key === 'K' || e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        queue.prev();
        setIndex(queue.getCurrentIndex());
      } else {
        const patch = queue.handleKeyAction(e.key);
        if (patch) {
          e.preventDefault();
          handleApplyPatch(patch);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [queue, handleApplyPatch]);

  if (pendingItems.length === 0 || !currentItem) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'var(--theme-bg-main)',
          color: 'var(--theme-text-primary)',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
        <h2 style={{ fontSize: 'var(--theme-text-xl)', fontWeight: 700, margin: '0 0 8px 0' }}>
          {t('triage.inboxZeroTitle') || 'Inbox Zero Reached!'}
        </h2>
        <p style={{ fontSize: 'var(--theme-text-sm)', color: 'var(--theme-text-secondary)', maxWidth: '400px' }}>
          {t('triage.inboxZeroMessage') || 'All active tasks have been triaged, scheduled, or resolved.'}
        </p>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={t('triage.queueTitle') || 'Triage Queue'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        backgroundColor: 'var(--theme-bg-main)',
        color: 'var(--theme-text-primary)',
        padding: '24px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* Top Progress Bar */}
      <div style={{ width: '100%', maxWidth: '650px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--theme-text-xs)', fontWeight: 600, color: 'var(--theme-text-secondary)', marginBottom: '8px' }}>
          <span>{t('triage.queueTitle') || 'Triage Queue'}</span>
          <span>
            {t('triage.progressSummary', { current: progress.current, total: progress.total, percent: progress.percent }) ||
              `Item ${progress.current} of ${progress.total} (${progress.percent}%)`}
          </span>
        </div>
        <div style={{ height: '4px', backgroundColor: 'var(--theme-bg-subtle)', borderRadius: '2px', overflow: 'hidden' }}>
          <div
            style={{
              width: `${progress.percent}%`,
              height: '100%',
              backgroundColor: 'var(--theme-accent)',
              transition: 'width var(--theme-transition-fast)',
            }}
          />
        </div>
      </div>

      {/* Focused Center Card */}
      <div
        tabIndex={0}
        role="article"
        aria-label={`${currentItem.title} (${currentItem.type})`}
        style={{
          width: '100%',
          maxWidth: '650px',
          backgroundColor: 'var(--theme-bg-card)',
          border: '1px solid var(--theme-border)',
          borderRadius: 'var(--theme-radius-xl, 16px)',
          padding: '28px',
          boxShadow: 'var(--theme-shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Meta Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            style={{
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 700,
              textTransform: 'uppercase',
              backgroundColor: 'var(--theme-accent-subtle)',
              color: 'var(--theme-accent)',
              padding: '3px 8px',
              borderRadius: 'var(--theme-radius-sm, 4px)',
            }}
          >
            {t(`taskflow.types.${currentItem.type}`) || currentItem.type}
          </span>

          <span
            style={{
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 700,
              textTransform: 'uppercase',
              backgroundColor: 'var(--theme-bg-subtle)',
              color: 'var(--theme-text-muted)',
              padding: '3px 8px',
              borderRadius: 'var(--theme-radius-sm, 4px)',
            }}
          >
            {t('common.status')}: {t(`taskflow.statuses.${currentItem.status}`) || currentItem.status}
          </span>
        </div>

        {/* Big Title */}
        <h1
          style={{
            fontSize: 'var(--theme-text-2xl)',
            fontWeight: 700,
            color: 'var(--theme-text-primary)',
            margin: '4px 0',
            lineHeight: 'var(--theme-leading-tight)',
          }}
        >
          <bdi>{currentItem.title}</bdi>
        </h1>

        {/* Description */}
        {currentItem.description && (
          <p style={{ fontSize: 'var(--theme-text-base)', color: 'var(--theme-text-secondary)', lineHeight: 'var(--theme-leading-relaxed)', margin: 0 }}>
            <bdi>{currentItem.description}</bdi>
          </p>
        )}

        {/* Tags & Due Date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
          {currentItem.dueDate && (
            <span style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 600, color: 'var(--theme-text-muted)', backgroundColor: 'var(--theme-bg-subtle)', padding: '2px 8px', borderRadius: 'var(--theme-radius-sm, 4px)' }}>
              📅 {t('taskflow.dueDate')}: {formatDate(currentItem.dueDate)}
            </span>
          )}

          <span style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 600, color: 'var(--theme-text-muted)', backgroundColor: 'var(--theme-bg-subtle)', padding: '2px 8px', borderRadius: 'var(--theme-radius-sm, 4px)' }}>
            {t('kanban.priority')}: {t(`taskflow.priorities.${currentItem.priority || 'normal'}`) || (currentItem.priority || 'normal')}
          </span>

          {currentItem.tags?.map((tag) => (
            <span key={tag} style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-accent)', backgroundColor: 'var(--theme-accent-subtle)', padding: '2px 8px', borderRadius: 'var(--theme-radius-sm, 4px)' }}>
              <bdi>#{tag}</bdi>
            </span>
          ))}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: 'var(--theme-border-subtle)', margin: '8px 0' }} />

        {/* Interactive Quick Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
          {[
            { key: '1', label: t('taskflow.statuses.backlog') || 'Backlog', status: 'backlog' },
            { key: '2', label: t('taskflow.statuses.inProgress') || 'In Progress', status: 'in-progress' },
            { key: '3', label: t('taskflow.statuses.blocked') || 'Blocked', status: 'blocked' },
            { key: '4', label: t('taskflow.statuses.review') || 'Review', status: 'review' },
            { key: '5', label: t('taskflow.statuses.done') || 'Done', status: 'done', done: true },
          ].map((act) => (
            <button
              key={act.key}
              aria-label={`Set status ${act.label} (Key ${act.key})`}
              onClick={() => handleApplyPatch(act.done ? { status: act.status, progressPercentage: 100 } : { status: act.status })}
              style={{
                padding: '8px 4px',
                backgroundColor: 'var(--theme-bg-subtle)',
                border: '1px solid var(--theme-border)',
                borderRadius: 'var(--theme-radius-md, 8px)',
                color: 'var(--theme-text-primary)',
                fontSize: 'var(--theme-text-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                transition: 'background-color var(--theme-transition-fast)',
              }}
            >
              <span style={{ fontSize: '10px', opacity: 0.6 }}>[{act.key}]</span>
              <span>{act.label}</span>
            </button>
          ))}
        </div>

        {/* Utility Buttons: Priority Cycle, Defer, Archive, Open Drawer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              aria-label="Cycle priority (P)"
              onClick={() => {
                const patch = queue.handleKeyAction('P');
                if (patch) handleApplyPatch(patch);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--theme-bg-subtle)',
                border: '1px solid var(--theme-border)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                fontSize: 'var(--theme-text-xs)',
                color: 'var(--theme-text-secondary)',
                cursor: 'pointer',
              }}
            >
              [P] {t('triage.cyclePriority') || 'Cycle Priority'}
            </button>

            <button
              aria-label="Defer due date by 1 day (D)"
              onClick={() => {
                const patch = queue.handleKeyAction('D');
                if (patch) handleApplyPatch(patch);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--theme-bg-subtle)',
                border: '1px solid var(--theme-border)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                fontSize: 'var(--theme-text-xs)',
                color: 'var(--theme-text-secondary)',
                cursor: 'pointer',
              }}
            >
              [D] {t('triage.deferDay') || 'Defer +1d'}
            </button>

            <button
              aria-label="Archive item (A)"
              onClick={() => handleApplyPatch({ status: 'archived' })}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--theme-status-danger-bg)',
                border: 'none',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                fontSize: 'var(--theme-text-xs)',
                color: 'var(--theme-status-danger-text)',
                cursor: 'pointer',
              }}
            >
              [A] {t('triage.archive') || 'Archive'}
            </button>
          </div>

          <button
            aria-label="Open detailed view for this task"
            onClick={() => onSelectTask(currentItem)}
            style={{
              padding: '6px 12px',
              background: 'none',
              border: 'none',
              color: 'var(--theme-accent)',
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('triage.openDetails') || 'Open Details ➔'}
          </button>
        </div>
      </div>

      {/* Navigation Footnote */}
      <div style={{ marginTop: '16px', display: 'flex', gap: '16px', fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)' }}>
        <span>{t('triage.navigateHelp') || 'Navigate: J (Next) / K (Previous)'}</span>
      </div>
    </div>
  );
};
